import { Injectable } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { EmailValidator, InputType, LuFormlyInputBuilder } from 'lu-formly';
import { LuFormlyAdvancedPasswordBuilder } from 'src/app/angie-shared/components/advanced-password/builders/advanced-password.builder';
import { MetaService } from 'src/app/angie-shared/services';
import { StateService } from 'src/app/core';
import { inputFieldsValidationConstants } from 'src/app/globals';
import { AuthService } from './auth.service';

@Injectable()
export class AuthConfigService {
	constructor(
		private authService: AuthService,
		private translateService: TranslateService,
		private stateService: StateService,
		private metaService: MetaService
	) {}

	getEmailOrUsername(login_with_usernames = false, authCode = false): FormlyFieldConfig {
		if (!login_with_usernames) {
			return this.email(authCode);
		}
		return this.username();
	}
	getPassword(passwordLabel = 'login.password'): FormlyFieldConfig {
		const password = new LuFormlyInputBuilder('password', this.translateService.instant(passwordLabel))
			.setInputType(InputType.PASSWORD)
			.setValidators([Validators.required])
			.setRequired(true)
			.setAttributes({ 'qa-id': 'password_field' })
			.build();
		return password;
	}
	getAdvancedAndConfirmPassword(
		form: UntypedFormGroup,
		passwordLabel = 'lup.login.confirm_password_label',
		advancedPasswordlabel = 'login.password'
	): FormlyFieldConfig[] {
		return [this.advancedPassword(advancedPasswordlabel, form), this.confirmPassword(passwordLabel, form)];
	}

	getCodeRedeem(): FormlyFieldConfig {
		const codeRedeem = new LuFormlyInputBuilder('code', this.translateService.instant('coupons.code'))
			.setValidators([Validators.required])
			.setAttributes({ 'qa-id': 'code_field' })
			.setRequired(true)
			.setMaxLength(inputFieldsValidationConstants.MAX_TEXT_INPUT_LENGTH)
			.hideRequiredMarker(true)
			.build();
		return codeRedeem;
	}

	setMetaTitle(text: string): void {
		this.metaService.setData({ title: this.translateService.instant(text) });
	}

	private email(hasAuthCode: boolean): FormlyFieldConfig {
		const email = new LuFormlyInputBuilder('email', this.translateService.instant('login.email'))
			.setValidators([EmailValidator])
			.setAttributes({ 'qa-id': 'email_field' })
			.setRequired(true)
			.setMaxLength(inputFieldsValidationConstants.MAX_TEXT_INPUT_LENGTH)
			.setReadonly(hasAuthCode)
			.setDefaultValue(hasAuthCode ? this.authService.authData?.invite?.email : '')
			.hideRequiredMarker(true)
			.build();
		return email;
	}
	private username(): FormlyFieldConfig {
		const username = new LuFormlyInputBuilder('username', this.translateService.instant('login.username'))
			.setInputType(InputType.TEXT)
			.setValidators([Validators.required])
			.setAttributes({ 'qa-id': 'email_field' })
			.setRequired(true)
			.build();
		return username;
	}

	private advancedPassword(passwordLabel = 'login.password', form: UntypedFormGroup): FormlyFieldConfig {
		const { password_policy_configuration } = this.authService.authData || this.stateService.portalMeta;

		const advancedPassword = new LuFormlyAdvancedPasswordBuilder(
			'advancedPassword',
			this.translateService.instant(passwordLabel)
		)
			.setAttributes({
				autocomplete: 'new-password'
			})
			.setRequired(true)
			.setValidators([Validators.required])
			.hideRequiredMarker(true)
			.setConfig({ ...password_policy_configuration, layoutType: 'tooltip' })
			.build();
		advancedPassword.templateOptions.change = () => {
			form.get('confirmPassword').updateValueAndValidity();
		};
		return advancedPassword;
	}

	private confirmPassword(
		passwordLabel = 'lup.login.confirm_password_label',
		form: UntypedFormGroup
	): FormlyFieldConfig {
		const confirmPassword = new LuFormlyInputBuilder('confirmPassword', this.translateService.instant(passwordLabel))
			.setAttributes({
				autocomplete: 'new-password'
			})
			.setValidators([Validators.required])
			.setInputType(InputType.PASSWORD)
			.setRequired(true)
			.hideRequiredMarker(true)
			.setValidatorObject({
				passwordMatch: {
					message: this.translateService.instant('userz.passwords_not_maching'),
					expression: () => {
						return form.get('advancedPassword').value === form.get('confirmPassword').value;
					}
				}
			})
			.build();
		return confirmPassword;
	}
}
